import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel } from "@/components/app/common";
import { EntityPicker, customerRender } from "@/components/app/EntityPicker";
import { RegSitePicker, defaultIds } from "@/components/order/RegSitePicker";
import { HeaderFields } from "@/components/order/HeaderFields";
import { PoDuplicateNote, usePoDuplicates } from "@/components/order/PoDuplicateNote";
import { ConfirmationDocs, uploadStaged } from "@/components/order/ConfirmationDocs";
import { InvoicePlanEditor } from "@/components/order/InvoicePlan";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { DEFAULTS, GROUPS } from "@/lib/orderFields";
import { FamilyFields } from "@/components/order/FamilyFields";

const FAMILY_OF = (p) => (p === "LMO" || p === "LMS" ? [p] : ["LMP", "LMR", "LMPP"]);
const TITLE = { LMO: "New LMO - contract (Rental & Operate / AMC)", LMS: "New LMS - service action" };

export default function OrderNew() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const want = (sp.get("type") || "LMR").toUpperCase();
  const [customer, setCustomer] = useState(null);
  const [ids, setIds] = useState({ regId: null, siteId: null });
  const [f, setF] = useState({ ...DEFAULTS });
  const { data: allTypes } = useFetch("/lm-types?active=true");
  const types = (allTypes || []).filter((t) => FAMILY_OF(want).includes(t.prefix));
  const type = (types || []).find((t) => t.prefix === f.prefix);
  const proj = f.is_projection;
  const pickType = useCallback((p) => {
    const t = (allTypes || []).find((x) => x.prefix === p);
    if (!t) return;
    setF((o) => {
      if (t.projection_by_default) return { ...o, prefix: p, is_projection: true, confirmation_type: "Projection", po_status: "Pending" };
      const fix = o.confirmation_type === "Projection" ? { confirmation_type: "PO", po_status: "Received" } : {};
      return { ...o, prefix: p, is_projection: false, ...fix };
    });
  }, [allTypes]);
  const hasPrefix = !!f.prefix;
  const first = (types.find((t) => t.prefix === want) || types[0])?.prefix;
  useEffect(() => {
    if (first && !hasPrefix) pickType(first);
  }, [first, hasPrefix, pickType]);
  const [busy, setBusy] = useState(false);
  const [staged, setStaged] = useState({});
  const poDups = usePoDuplicates(f.customer_po_no, customer?.id);
  const reg = customer?.registrations?.find((r) => r.id === ids.regId);
  const pickCustomer = useCallback((c) => {
    setCustomer(c);
    if (!c) {
      setIds(defaultIds(null));
      setF((o) => ({ ...o, projection_ref_id: "", asset_group_id: "", asset_id: "" }));
      return;
    }
    setIds(defaultIds(c));
    setF((o) => ({ ...o, projection_ref_id: "", asset_group_id: "", asset_id: "", segment: c.segment || "", contact_person: o.contact_person || c.contact_name || "", contact_no: o.contact_no || c.phone || "" }));
  }, []);
  useEffect(() => {
    const cid = sp.get("customer_id");
    if (!cid) return;
    api.get(`/customers/${cid}`).then((r) => { pickCustomer(r.data); const sid = sp.get("site_id"); if (sid && (r.data.sites || []).some((s) => s.id === sid)) setIds((x) => ({ ...x, siteId: sid })); setF((o) => ({ ...o, asset_group_id: sp.get("asset_group_id") || "" })); }).catch(() => {});
  }, [sp, pickCustomer]);
  const save = async () => {
    if (!customer) return toast.error("Pick a customer from the Customers Master");
    setBusy(true);
    try {
      const r = await api.post("/orders", { ...f, customer_id: customer.id, registration_id: ids.regId, site_id: ids.siteId });
      const { Contract, ...rest } = staged;
      try { await uploadStaged(r.data.id, f.prefix === "LMO" && Contract ? staged : rest); } catch (e) { toast.error(`LM saved, but a file did not upload: ${errMsg(e)}`); }
      toast.success(`${r.data.order_no} created — ${f.prefix === "LMO" ? "add billing heads and any asset / dispatch items (items are optional)" : "add the line items next"}`);
      navigate(`/orders/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <div className="pb-20" data-testid="order-new-page">
      <PageHeader title={TITLE[want] || "New LM - regular order (LMP / LMR / LMPP)"} subtitle="Customer, GSTIN and addresses come from the Customers Master. Values are basic (excl. GST)." />
      <Panel title="Order header" className="mb-4" testid="lm-section-header">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 mb-3">
          <div><Label>LM type *</Label>
            <Select value={f.prefix || ""} onValueChange={pickType}>
              <SelectTrigger className="bg-white" data-testid="lm-prefix"><SelectValue placeholder="From LM Type master" /></SelectTrigger>
              <SelectContent>{(types || []).map((t) => <SelectItem key={t.prefix} value={t.prefix}>{t.prefix} — {t.description}</SelectItem>)}</SelectContent>
            </Select>
            {type && <p className="text-[11px] text-slate-500 mt-1" data-testid="lm-type-rules">{type.needs_approval ? "Needs approval" : "Auto-approved on submit"}{type.projection_by_default ? " · projection by default" : ""}</p>}</div>
          <div><Label>Record type</Label>
            <div className="h-9 flex items-center rounded border border-slate-200 bg-slate-50 px-3 text-sm" data-testid="lm-is-projection">{proj ? "Projection (PO pending) - from LMPP type" : "Confirmed order"}</div></div>
          <div><Label>LM number</Label><div className="h-9 flex items-center rounded border border-slate-200 bg-slate-50 px-3 font-mono text-sm text-slate-500" data-testid="lm-number-preview">{type ? `${type.preview} (next, on save)` : "—"}</div></div>
          <div className="col-span-2 md:col-span-3 xl:col-span-1">
            <Label>Customer *</Label>
            <EntityPicker url="/customers?active=true" value={customer} onChange={pickCustomer} render={customerRender} placeholder="Search by name, alias, code or GSTIN" testid="order-customer-picker" />
            <p className="text-xs text-slate-500 mt-1">Not found? <Link to="/masters/customers?new=1" className="text-sky-700 underline" data-testid="create-customer-in-master-link">Create it in Customers Master</Link> first.</p>
            {reg && !reg.gstin && <p className="text-xs text-amber-700 mt-1" data-testid="order-gst-pending-note">This registration has no GSTIN. The LM will be flagged “GST pending”.</p>}
          </div>
          {customer && <div className="col-span-2 md:col-span-3 xl:col-span-4 grid grid-cols-2 gap-3"><RegSitePicker customer={customer} ids={ids} onChange={setIds} /></div>}
        </div>
        <HeaderFields group={GROUPS[0]} f={f} setF={setF} />
      </Panel>
      <FamilyFields f={f} setF={setF} customerId={customer?.id} customer={customer} siteId={ids.siteId} staged={staged} setStaged={setStaged} />
      {GROUPS.slice(1).map((g) => (
        <Panel key={g.id} title={g.title} className="mb-4" testid={`lm-section-${g.id}`}>
          <HeaderFields group={g} f={f} setF={setF} customerId={customer?.id} />
          {g.id === "po" && <div className="mt-3"><ConfirmationDocs staged={staged} setStaged={setStaged} poRequired={!proj && f.confirmation_type === "PO"} /></div>}
          {g.id === "plan" && <div className="mt-4 border-t border-slate-100 pt-3"><div className="text-xs font-semibold text-slate-700 mb-2">{f.prefix === "LMO" ? "Service invoicing (LMO contract)" : "Invoicing"}</div>
            {f.prefix === "LMO" && <p className="mb-2 text-xs text-slate-500" data-testid="lm-lmo-invoicing-note">Invoices here are for the contract service. Equipment sent to site goes on a delivery challan, not an invoice.</p>}
            <InvoicePlanEditor mode={f.invoicing_mode || "single"} rows={f.invoice_plan || []} onChange={(m, rows) => setF({ ...f, invoicing_mode: m, invoice_plan: rows })} /></div>}
          {proj && g.id === "po" && <p className="mt-3 text-xs text-violet-800 bg-violet-50 border border-violet-200 rounded px-3 py-2" data-testid="lm-projection-note">Projection order: quasi-confirmed, PO not yet received. No PO number needed to submit; it books capacity but is never dispatched. Each PO is booked later as its own LM “against projection”, which reduces this projection's balance.</p>}
          {g.id === "po" && <div className="mt-3"><PoDuplicateNote rows={poDups} testid="order-po-duplicate-warning" /></div>}
        </Panel>
      ))}
      <div className="fixed bottom-0 right-0 left-0 lg:left-[250px] z-20 flex justify-end gap-2 border-t border-slate-200 bg-white/95 px-6 py-3 backdrop-blur">
        <Button variant="outline" onClick={() => navigate("/orders")} data-testid="order-cancel-btn">Cancel</Button>
        <Button onClick={save} disabled={busy} data-testid="order-create-btn">{busy ? "Creating…" : "Save draft LM"}</Button>
      </div>
    </div>
  );
}
